import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DialogShellComponent } from '@custom-components/overlay/dialog-shell/dialog-shell.component';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import { FilledButtonComponent, TextButtonComponent } from '@custom-components/general/button';
import { DeviceConfigurationClear } from '@core/models';
import { DeviceService } from '@core/services';
import { TranslateModule } from '@ngx-translate/core';
import { DeviceStore } from '@root/app/store/device.store';
import { concatMap, first, interval, switchMap } from 'rxjs';

@Component({
  selector: 'xemex-device-configuration-clear-dialog',
  imports: [
    MatIconModule,
    MatDialogModule,
    TranslateModule,
    DialogShellComponent,
    SpinnerComponent,
    FilledButtonComponent,
    TextButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './clear-device-configuration-dialog.component.html',
})
export class DeviceConfigurationClearDialogComponent {
  readonly deviceStore = inject(DeviceStore);
  readonly dialogRef = inject(MatDialogRef<DeviceConfigurationClearDialogComponent>);
  public data = inject(MAT_DIALOG_DATA);
  private readonly deviceService = inject(DeviceService);

  channelNumber: number;
  isClearing = false;
  clearError: string | null = null;

  constructor() {
    this.channelNumber = this.data.channelNumber;
  }

  onClear() {
    const deviceId = this.deviceStore.deviceDetail()!.deviceId;
    const deviceConfigurationClear: DeviceConfigurationClear = {
      channelNumber: this.channelNumber,
    };

    this.isClearing = true;
    this.clearError = null;

    this.deviceService
      .clearDeviceConfigurationFileFromDevice(deviceId, deviceConfigurationClear)
      .pipe(
        switchMap(({ deviceConfigDispatchId }) =>
          interval(1000).pipe(
            concatMap(() => this.deviceService.sendDeviceConfigurationDispatch(deviceConfigDispatchId)),
            first(data => !['Pending', 'In Progress'].includes(data.status)),
          ),
        ),
      )
      .subscribe({
        next: data => {
          this.isClearing = false;
          if (data.status === 'Success') {
            this.deviceStore.getChannelConfigurationMethod(deviceId);
            this.dialogRef.close(null);
          } else {
            this.clearError = data.error ?? 'An unexpected error occurred';
          }
        },
        error: err => {
          this.isClearing = false;
          this.clearError = err?.error?.message ?? err?.message ?? 'An unexpected error occurred';
        },
      });
  }
}
